import React from "react";
import { useRequest } from "ahooks";
import { api } from "@redux/api";
import { useAuth } from "@/hooks/use-auth";
import { Nopermission } from "@/components/Nopermission";
import { useNavigate } from "react-router-dom";
import { emitEvent } from "@/hooks/useEventBus";
import { useDispatch, useSelector } from "react-redux";
import appActions from "@redux/app/actions";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";
import SwitchablePieChart from "./SwitchablePieChart";
import AiChartContextRegistration from "@/containers/facility/AiAssistant/AiChartContextRegistration";

const EquipmentChart = () => {
  const equipmentPermission = useAuth("equipment", 1);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const facilityId = useSelector((state) => state?.Facility?.selected?.Id);
  const facilityContext = useSelector((state) => getFacilityContext(state));

  const { data: equipmentData } = useRequest(
    () => api.allequipmentview(),
    {
      ready: equipmentPermission,
      cacheKey: `equipment-combined-chart-data-${facilityId}`,
      staleTime: 1 * 60 * 1000,
    }
  );

  if (!equipmentPermission) {
    return <Nopermission />;
  }

  const equipmentCategoryData = equipmentData?.data?.grouped_category_data;
  const equipmentConditionData = equipmentData?.data?.grouped_status_data;

  if (!equipmentCategoryData && !equipmentConditionData) {
    return null;
  }

  const handleCategoryClick = (e) => {
    emitEvent("CLOSE_EQUIPMENT");
    const url =
      buildFacilityPath("equipment", {
        ...facilityContext,
        query: { category: e.data.name },
      }) || "/switch-view";
    navigate(url);
    dispatch(appActions.changeCurrent(["equipment"]));
  };

  const handleConditionClick = (e) => {
    emitEvent("CLOSE_EQUIPMENT");
    const url =
      buildFacilityPath("equipment", {
        ...facilityContext,
        query: { condition: e.data.name },
      }) || "/switch-view";
    navigate(url);
    dispatch(appActions.changeCurrent(["equipment"]));
  };

  const configs = [
    {
      key: "category",
      title: "Equipment By Category",
      label: "Category",
      data: equipmentCategoryData,
      colors: undefined,
      onClick: handleCategoryClick,
    },
    {
      key: "condition",
      title: "Equipment By Condition",
      label: "Condition",
      data: equipmentConditionData,
      colors: undefined,
      onClick: handleConditionClick,
    },
  ];

  return (
    <>
      {equipmentCategoryData ? (
        <AiChartContextRegistration
          context={{
            pageId: "page.facility.dashboard",
            chart: {
              chartId: "dashboard.equipment.category",
              title: "Equipment By Category",
              aliases: ["equipment category", "category pie", "设备类别图", "设备分类分布"],
              chartType: "pie",
              description: "Current equipment count grouped by category.",
              sourceOperationIds: ["equipment.group_by_category"],
              series: [{
                name: "Equipment",
                unit: "count",
                points: equipmentCategoryData.map((item) => ({ label: item.type, value: item.value })),
              }],
              completeness: "complete",
            },
          }}
        />
      ) : null}
      {equipmentConditionData ? (
        <AiChartContextRegistration
          context={{
            pageId: "page.facility.dashboard",
            chart: {
              chartId: "dashboard.equipment.condition",
              title: "Equipment By Condition",
              aliases: ["equipment condition", "condition pie", "设备状态图", "设备状况分布"],
              chartType: "pie",
              description: "Current equipment count grouped by condition.",
              sourceOperationIds: ["equipment.group_by_condition"],
              series: [{
                name: "Equipment",
                unit: "count",
                points: equipmentConditionData.map((item) => ({ label: item.type, value: item.value })),
              }],
              completeness: "complete",
            },
          }}
        />
      ) : null}
      <SwitchablePieChart configs={configs} defaultActiveKey="category" />
    </>
  );
};

export default EquipmentChart;
